import { EventEmitter, Injectable, signal, computed } from '@angular/core';
import {
  Brand,
  BrandRow,
  Client,
  JustBrand,
  SelectionSlug
} from '@portal/app/shared/types';
import { slugify } from '@portal/app/shared/helpers/helpers';
import { StorageService } from './storage.service';
import { split } from 'lodash-es';
import { demoDefaultClient } from '@portal/app/shared/constants/constants';
import { UserModel } from '@portal/app/shared/models/userModel';
import { Observable, throwError } from 'rxjs';
import { ClientStoreService } from '../../dashboard/geo-doe-config/data-stores/client-store.service';
import { CurrencyService } from '@portal/app/shared/services/currency.service';
import { CurrencySymbol } from '@portal/app/shared/types/currency-format';

@Injectable({
  providedIn: 'root'
})
export class SelectionService {
  private static readonly LAST_BRAND_KEY = 'lastSelectedBrand';

  // New reactive signal for the current selection.
  private _selection = signal<JustBrand>({ brand: null });
  private selection: JustBrand = { brand: null };

  public selectionLabel = '';
  public selectionChanged: EventEmitter<JustBrand> =
    new EventEmitter<JustBrand>();

  constructor(
    private readonly store: StorageService,
    private currencyService: CurrencyService,
    private clientStoreService: ClientStoreService
  ) {
    this.setSelectionLabel();
  }

  // Expose the reactive selection as a computed signal.
  clientBrandSignal = computed(() => {
    const brand = this._selection().brand;
    return {
      clientId: brand?.clientId || 0,
      brandId: brand?.brandId || 0,
      brandName: brand?.brandName || '',
      clientName: this.clientStoreService.client?.clientName || ''
    };
  });

  // Expose full brand as a signal
  brandSignal = computed(() => this._selection().brand);

  clearSelection(): void {
    this.clientStoreService.client = undefined;
    this.selection = { brand: null };
    this._selection.set({ brand: null });
  }

  setUpBrandCurrency(brand: Brand) {
    const currencyCode = brand.currencyCode ? brand.currencyCode : 'USD';
    const currencyDisplay: CurrencySymbol =
      this.currencyService.checkCurrencySymbol(brand);
    const currencyLocale = brand?.currencyLocale
      ? brand.currencyLocale
      : 'en-US';
    this.currencyService.updateCurrencyData({
      code: currencyCode,
      locale: currencyLocale,
      display: currencyDisplay
    });
  }

  setSelection(client: Client, brand: Brand): void {
    this.setUpBrandCurrency(brand);
    this.clientStoreService.client = client;
    this.selection = { brand };
    this._selection.set({ brand });
    this.setSelectionLabel();
    this.persistLastSelectedBrand(client.clientId, brand.brandId);
    // Emit an event for backward compatibility.
    if (client != null && brand != null) {
      this.selectionChanged.emit({ brand });
    }
  }

  getSelection(): JustBrand {
    return this.selection;
  }

  hasSelection(): boolean {
    return this.selection != null && this.selection.brand != null;
  }

  parseSelectionLabel(item: JustBrand | BrandRow = this.selection): string {
    if (item != null) {
      const { brand } = item;
      if (brand != null) {
        return brand?.brandName ? brand.brandName : '';
      }
    }
    return '';
  }

  buildSelectionSlug(item: JustBrand | BrandRow = this.selection): string {
    if (item != null) {
      const { brand } = item;
      const brandLabel = this.parseSelectionLabel(item);
      if (brand != null) {
        return `${brand.clientId}-${brand.brandId}-${slugify(brandLabel)}`;
      }
    }
    return '';
  }

  // Reverse of buildSelectionSlug.
  parseSelectionSlug(slug = ''): Observable<SelectionSlug> {
    const splitCharacter = '-';
    const splitItems = split(slug, splitCharacter);
    if (splitItems.length >= 3) {
      const clientId = Number(splitItems.shift());
      const brandId = Number(splitItems.shift());
      const brandLabel = splitItems.join(splitCharacter);
      return new Observable<SelectionSlug>((subscriber) => {
        subscriber.next({ brandId, clientId, brandLabel });
        subscriber.complete();
      });
    }
    return throwError(() => new Error('Brand Selection format is invalid'));
  }

  private setSelectionLabel(): void {
    this.selectionLabel = this.parseSelectionLabel();
  }

  getClientIdAndBrandId(): { clientId: number; brandId: number } {
    const brand = this.getSelection().brand;
    const clientId = brand?.clientId || 0;
    const brandId = brand?.brandId || 0;
    return { clientId, brandId };
  }

  resolveInitialBrand(
    user: UserModel,
    defaultClient: Client,
    defaultBrand: Brand,
    findMatch: (
      clientId: number,
      brandId: number
    ) => { client: Client; brand: Brand } | undefined
  ): { client: Client; brand: Brand } {
    const lastBrand = this.getLastSelectedBrand();
    if (lastBrand) {
      const saved = findMatch(lastBrand.clientId, lastBrand.brandId);
      if (saved) {
        return saved;
      }
    }

    if (user.isSuperUser(this) && user.isMeasuredUser()) {
      const demo = findMatch(
        demoDefaultClient.clientId,
        demoDefaultClient.brandId
      );
      if (demo) {
        return demo;
      }
    }

    return { client: defaultClient, brand: defaultBrand };
  }

  getLastSelectedBrand(): { clientId: number; brandId: number } | null {
    const stored = this.store.get(SelectionService.LAST_BRAND_KEY);
    if (stored) {
      try {
        const parsed = typeof stored === 'string' ? JSON.parse(stored) : stored;
        if (parsed?.clientId && parsed?.brandId) {
          return { clientId: parsed.clientId, brandId: parsed.brandId };
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  private persistLastSelectedBrand(clientId: number, brandId: number): void {
    this.store.set(
      SelectionService.LAST_BRAND_KEY,
      JSON.stringify({ clientId, brandId })
    );
  }
}
